import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { stuffedAnimalsList } from "../../data/stuffedAnimals";
import ImageWithTitle from "../../components/ImageWithTitle";
import Button from "../../components/Button";
import Row from "../../components/Row";
import Column from "../../components/Column";

import { en, es } from "../../languages/langs";
import { api, sanctum } from "../../services/api";
import { getCaptchaToken } from "../../services/captcha";
import {
  buildTaxidermyRegistration,
  validateRegistration,
} from "./taxidermyRegistration";

const EMPTY_FORM = {
  salutation: "Mr.",
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  passwordRepeat: "",
  countryId: "empty",
  city: "",
  mobilePhone: "",
  representsCompany: false,
  companyName: "",
  companyPhone: "",
};

// 422 keys the form has a slot for; anything else lands in the general line.
const KNOWN_FIELDS = [
  "title",
  "first_name",
  "last_name",
  "email",
  "password",
  "country",
  "city",
  "phone",
  "mobile_phone",
];

const RegisterForm = ({ activeLang, setLoggedInState }) => {
  const lang = { en, es }[activeLang];
  const history = useHistory();

  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [generalError, setGeneralError] = useState("");
  const [sending, setSending] = useState(false);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    const getCountries = async () => {
      try {
        const response = await api.get("/countries");
        setCountries(response.data);
      } catch (e) {
        console.error(e);
      }
    };
    getCountries();
  }, []);

  const update = (field) => (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // Unticking clears the company fields so stale text cannot survive
      // into the payload (same convention as Register.js's no-website box).
      if (field === "representsCompany" && !value) {
        next.companyName = "";
        next.companyPhone = "";
      }
      return next;
    });
  };

  const inputClass = (field) =>
    `register-form__input register-form__input--mandatory${
      fieldErrors[field] ? " register-form__input--has-error" : ""
    }`;

  // Values are either langs.js keys (client checks) or raw server strings.
  const errorFor = (field) =>
    fieldErrors[field] ? (
      <span className="register-form__field-error">
        {lang[fieldErrors[field]] || fieldErrors[field]}
      </span>
    ) : null;

  const applyServerErrors = (errors) => {
    const byField = {};
    const rest = [];
    Object.keys(errors || {}).forEach((key) => {
      const message = [].concat(errors[key]).join(" ");
      if (KNOWN_FIELDS.includes(key)) {
        byField[key] = message;
      } else {
        rest.push(message);
      }
    });
    setFieldErrors(byField);
    setGeneralError(rest.join(" "));
  };

  const logInNewAccount = async () => {
    try {
      await sanctum.get("/csrf-cookie");
      const response = await api.post("/login", {
        email: form.email.trim(),
        password: form.password,
      });
      if (response.data.success) {
        localStorage.setItem("accessToken", response.data.token);
        api.defaults.headers.common.Authorization = `Bearer ${response.data.token}`;
        setLoggedInState(true);
        return;
      }
      throw new Error("login rejected");
    } catch (e) {
      // The account exists (registration succeeded); hand over to the
      // normal login page rather than losing the visitor here.
      alert(lang["account_created_login"]);
      history.push("/login");
    }
  };

  const doRegister = async () => {
    setGeneralError("");
    const errors = validateRegistration(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    // Token only - verification is the backend's job; the secret does not
    // ship in this bundle. A null token (blocked script) still submits.
    const captchaToken = await getCaptchaToken();
    setSending(true);
    try {
      await sanctum.get("/csrf-cookie");
      const payload = buildTaxidermyRegistration(form);
      if (captchaToken) {
        payload.captcha_token = captchaToken;
      }
      const response = await api.post("/register-taxidermy", payload);
      // Email-taken arrives as HTTP 200 with success:false.
      if (response.data.success === false) {
        setFieldErrors({ email: response.data.message });
        return;
      }
      await logInNewAccount();
    } catch (e) {
      if (e.response && e.response.status === 422) {
        applyServerErrors(e.response.data.errors);
        return;
      }
      console.error(e);
      setGeneralError(lang["Error sending"]);
    } finally {
      setSending(false);
    }
  };

  const renderedAnimals = stuffedAnimalsList.map((mainEntry, i) => (
    <Column extraClasses="size__one-of-five p-right__x-large" key={i}>
      <ImageWithTitle
        image={mainEntry.bg}
        activeLang={activeLang}
        text={mainEntry.menuTitle}
        spanishText={mainEntry.menuTitleEs}
      />
    </Column>
  ));

  return (
    <React.Fragment>
      <section className="content-width center">
        <Row>
          <Column
            id="contact-form"
            extraClasses="size__two-of-three p-top__x-large p-right__x-large mobile-p-right__none"
          >
            <p className="stuffed-text">
              <span
                dangerouslySetInnerHTML={{ __html: lang["Stuffed we support"] }}
              />
            </p>
            <Row extraClasses="size__one-of-one">
              <p className="title">{lang["taxidermy_register_title"]}</p>
            </Row>
            <Row extraClasses="size__one-of-one">
              <span className="sub-title browngrey-text">
                {lang["taxidermy_register_sub"]}
              </span>
            </Row>
            <Row extraClasses="size__one-of-one m-top__medium">
              <Column extraClasses="size__two-of-twelve">
                <select
                  id="taxidermy-register__salutation"
                  className="register-form__salutation select--creme"
                  value={form.salutation}
                  onChange={update("salutation")}
                >
                  {["Mr.", "Mrs.", "Ms.", "Dr.", "Ing."].map((title) => (
                    <option key={title} value={title}>
                      {title}
                    </option>
                  ))}
                </select>
              </Column>
              <Column extraClasses="size__five-of-twelve p-left__medium mobile-p-left__none mobile-m-top__medium">
                <input
                  type="text"
                  maxLength={50}
                  className={inputClass("first_name")}
                  placeholder={`${lang["First Name"]}*`}
                  value={form.firstName}
                  onChange={update("firstName")}
                />
                {errorFor("first_name")}
              </Column>
              <Column extraClasses="size__five-of-twelve p-left__medium mobile-p-left__none mobile-m-top__medium">
                <input
                  type="text"
                  maxLength={50}
                  className={inputClass("last_name")}
                  placeholder={`${lang["Last Name"]}*`}
                  value={form.lastName}
                  onChange={update("lastName")}
                />
                {errorFor("last_name")}
              </Column>
            </Row>
            <Row extraClasses="m-top__medium">
              <input
                type="email"
                maxLength={150}
                className={inputClass("email")}
                placeholder={`${lang["Email address"]}*`}
                value={form.email}
                onChange={update("email")}
              />
              {errorFor("email")}
            </Row>
            <Row extraClasses="m-top__medium">
              <Column extraClasses="size__one-of-two">
                <input
                  type="password"
                  className={inputClass("password")}
                  placeholder={`${lang["Create password"]}*`}
                  value={form.password}
                  onChange={update("password")}
                />
                {errorFor("password")}
              </Column>
              <Column extraClasses="size__one-of-two p-left__medium mobile-p-left__none mobile-m-top__medium">
                <input
                  type="password"
                  className={inputClass("password_repeat")}
                  placeholder={`${lang["Repeat Password"]}*`}
                  value={form.passwordRepeat}
                  onChange={update("passwordRepeat")}
                />
                {errorFor("password_repeat")}
              </Column>
            </Row>
            <Row extraClasses="m-top__medium">
              <Column extraClasses="size__one-of-two">
                <select
                  id="taxidermy-register__country"
                  className={`register-form__country select--creme${
                    fieldErrors.country
                      ? " register-form__input--has-error"
                      : ""
                  }`}
                  value={form.countryId}
                  onChange={update("countryId")}
                >
                  <option key="empty" value="empty">
                    {`${lang["Country"]}*`}
                  </option>
                  {countries.map((country) => (
                    <option key={country.country_code} value={country.id}>
                      {activeLang === "en" ? country.name : country.spanish_name}
                    </option>
                  ))}
                </select>
                {errorFor("country")}
              </Column>
              <Column extraClasses="size__one-of-two p-left__medium mobile-p-left__none mobile-m-top__medium">
                <input
                  type="text"
                  maxLength={30}
                  className="register-form__input"
                  placeholder={lang["City"]}
                  value={form.city}
                  onChange={update("city")}
                />
                {errorFor("city")}
              </Column>
            </Row>
            <Row extraClasses="m-top__medium">
              <Column extraClasses="size__one-of-two">
                <input
                  type="text"
                  maxLength={30}
                  className="register-form__input"
                  placeholder="Whatsapp"
                  value={form.mobilePhone}
                  onChange={update("mobilePhone")}
                />
                {errorFor("mobile_phone")}
              </Column>
            </Row>
            <Row extraClasses="m-top__medium">
              <Column extraClasses="size__one-of-one">
                <label
                  htmlFor="taxidermy-register__company"
                  className="checkbox-label"
                >
                  <input
                    type="checkbox"
                    id="taxidermy-register__company"
                    checked={form.representsCompany}
                    onChange={update("representsCompany")}
                  />
                  <span>{lang["I represent a company"]}</span>
                </label>
              </Column>
            </Row>
            {form.representsCompany && (
              <Row extraClasses="m-top__medium">
                <Column extraClasses="size__one-of-two">
                  <input
                    type="text"
                    maxLength={255}
                    className={inputClass("organisation_name")}
                    placeholder={`${lang["Company name"]}*`}
                    value={form.companyName}
                    onChange={update("companyName")}
                  />
                  {errorFor("organisation_name")}
                </Column>
                <Column extraClasses="size__one-of-two p-left__medium mobile-p-left__none mobile-m-top__medium">
                  <input
                    type="text"
                    className="register-form__input"
                    placeholder={lang["Company phone"]}
                    value={form.companyPhone}
                    onChange={update("companyPhone")}
                  />
                  {errorFor("phone")}
                </Column>
              </Row>
            )}
            {generalError && (
              <Row extraClasses="m-top__medium">
                <span className="register-form__field-error">
                  {generalError}
                </span>
              </Row>
            )}
            <Row extraClasses="m-top__large m-bottom__large">
              <Button onClick={doRegister} disabled={sending}>
                {lang["Register"]}
              </Button>
            </Row>
          </Column>
          <Column extraClasses="size__one-of-three p-left__medium mobile-p-right__none mobile-p-left__none">
            <div className="framed-image framed-image--xx-largeheight m-top__medium pull-up--mobile-none">
              <div className="framed-image__frame">
                <div
                  className="framed-image__image"
                  style={{ backgroundImage: "url('images/two_owl_icon.jpg')" }}
                ></div>
              </div>
            </div>
            <span
              style={{ fontStyle: "italic", display: "block" }}
              className="m-top__medium"
            >
              {lang["Diorama showing eagle owls with prey"]}
            </span>
          </Column>
        </Row>
      </section>
      <Row extraClasses="p-top__large p-bottom__large content-width center mobile-layout--hor-scroll">
        {renderedAnimals}
      </Row>
    </React.Fragment>
  );
};

export default RegisterForm;
